import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  fetchMdtRoles,
  createUser,
  updateUser,
} from "../../../redux/actions/userActions";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import ChipStatus from "../../common/ChipStatus";
import { scaleImageToFixedSize } from "../../common/ImageResize"; // We'll define a helper
import TextField from "../../common/TextField";
import IconButton from "../../common/Button";
import {
  SyncIcon,
  DeleteIcon,
  UserAvatar,
  UserAvatarAddButton,
} from "../../common/Icons";
import { generatePasswordResetLink } from "../../../services/userService";

const UserModal = ({
  isOpen,
  onClose,
  mode = "add", // "add" or "edit"
  initialValues = {},
  onDeleteUser,
}) => {
  const dispatch = useDispatch();
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const handleConfirmSave = () => {
    setShowSaveConfirm(false);
    handleSave();
  };

  // Basic form states
  const [name, setName] = useState(initialValues.name || "");
  const [email, setEmail] = useState(initialValues.email || "");
  // Use a single selected role; if editing, default to the first role (if any)
  const [selectedRole, setSelectedRole] = useState(
    initialValues.mdt_roles && initialValues.mdt_roles.length > 0
      ? initialValues.mdt_roles[0]
      : null
  );
  const [isActiveOfficer, setIsActiveOfficer] = useState(
    initialValues.is_active_officer || false
  );
  const [isAdmin, setIsAdmin] = useState(initialValues.is_admin || false);
  const [mdtRoles, setMdtRoles] = useState([]); // All available roles
  const [showResetLink, setShowResetLink] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Avatar states
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(
    initialValues.profile_picture || ""
  );
  const [removeProfilePic, setRemoveProfilePic] = useState(false);
  const fileInputRef = useRef(null);

  // We'll define our fixed size
  const FIXED_SIZE = 140;

  // Fetch MDT roles on mount
  useEffect(() => {
    let isMounted = true; // Add a flag to track if the component is still mounted

    const fetchRoles = async () => {
      const rolesData = await dispatch(fetchMdtRoles());
      if (isMounted) {
        setMdtRoles(rolesData.payload);
      }
    };

    fetchRoles();

    return () => {
      isMounted = false; // Cleanup function to set the flag to false
    };
  }, [dispatch]);

  // Clean up the avatar preview URL on unmount
  useEffect(() => {
    return () => {
      if (avatarPreview && typeof avatarPreview === "object") {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  // If editing, reset form fields when modal opens
  useEffect(() => {
    if (mode === "edit" && isOpen) {
      setName(initialValues.name || "");
      setEmail(initialValues.email || "");
      setSelectedRole(
        initialValues.mdt_roles && initialValues.mdt_roles.length > 0
          ? initialValues.mdt_roles[0]
          : null
      );
      setIsActiveOfficer(initialValues.is_active_officer || false);
      setIsAdmin(initialValues.is_admin || false);
      setAvatarPreview(initialValues.profile_picture || "");
    }
  }, [
    mode,
    isOpen,
    initialValues.name,
    initialValues.email,
    initialValues.mdt_roles,
    initialValues.is_active_officer,
    initialValues.is_admin,
    initialValues.profile_picture,
  ]);

  // When user picks a new file
  const handleAvatarChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setRemoveProfilePic(false);
    }
  };

  // Open the file picker
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Actually create or update user
  const handleSave = async () => {
    try {
      let scaledFile = null;
      if (avatarFile) {
        scaledFile = await scaleImageToFixedSize(
          avatarFile,
          FIXED_SIZE,
          FIXED_SIZE
        );
      }

      const userData = {
        name,
        email,
        mdt_roles: [selectedRole],
        is_active_officer: mode === "edit" ? isActiveOfficer : true,
        is_admin: isAdmin,
        avatarFile: scaledFile,
      };

      if (removeProfilePic) {
        userData.remove_profile_picture = true;
      }

      if (mode === "add") {
        await dispatch(createUser(userData));
      } else {
        await dispatch(
          updateUser({ userId: initialValues.id, formData: userData })
        );
      }

      onClose();
    } catch (err) {
      console.error("Error saving user:", err);
      // Optionally, show an error message to the user
    }
  };

  // Form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setShowSaveConfirm(true);
  };

  // Reset password logic
  const handleResetPassword = async () => {
    const { reset_link } = await generatePasswordResetLink(initialValues.id);
    setShowResetLink(true);
    setResetLink(reset_link);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(resetLink)
      .then(() => {
        setShowCopiedTooltip(true);
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  // If removing the picture
  const handleRemovePicChange = (event) => {
    setRemoveProfilePic(event.target.checked);
    if (event.target.checked) {
      setAvatarFile(null);
      setAvatarPreview("");
    }
  };

  // Footer buttons
  const footerButtons = [
    { label: "Cancel", onClick: onClose, variant: "secondary" },
    {
      label: mode === "add" ? "Add user" : "Update",
      onClick: () => document.getElementById("userForm")?.requestSubmit(),
      variant: "primary",
    },
  ];

  return (
    <>
      {isOpen && (
        <>
          <FullScreenOverlay
            title={mode === "add" ? "Add user details" : "Edit user details"}
            onClose={onClose}
            width="712px"
            height="auto"
            isPopup={false}
            iconClose={true}
            isSlimModal={true}
            footerButtons={footerButtons}
          >
            <form id="userForm" onSubmit={handleFormSubmit}>
              <Content>
                {/* Avatar Section */}
                <AvatarContainer>
                  <Avatar style={{ width: FIXED_SIZE, height: FIXED_SIZE }}>
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "50%",
                        }}
                      />
                    ) : (
                      <UserAvatar />
                    )}
                  </Avatar>
                  <AddAvatarButton type="button" onClick={openFilePicker}>
                    <UserAvatarAddButton />
                  </AddAvatarButton>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                  />
                </AvatarContainer>

                {/* Remove pic if editing */}
                {mode === "edit" &&
                  (initialValues.profile_picture || avatarPreview) && (
                    <RemovePicWrapper>
                      <label>
                        <input
                          type="checkbox"
                          checked={removeProfilePic}
                          onChange={handleRemovePicChange}
                        />
                        Remove existing picture
                      </label>
                    </RemovePicWrapper>
                  )}

                <FormGroup>
                  <FieldGroup>
                    <label>Name</label>
                    <TextField
                      type="text"
                      placeholder="Enter name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <label>Email address</label>
                    <TextField
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </FieldGroup>
                </FormGroup>

                {/* Roles */}
                <Section>
                  <SectionHeader className="body-text-regular semi-bold neutral-900">
                    MDT role
                  </SectionHeader>
                  <RoleGrid>
                    {mdtRoles.map((role) => (
                      <label htmlFor={`role-${role.id}`} key={role.id}>
                        <input
                          id={`role-${role.id}`}
                          type="radio"
                          name="mdt_role"
                          checked={selectedRole === role.name}
                          onChange={() => setSelectedRole(role.name)}
                          required
                        />
                        {role.name}
                      </label>
                    ))}
                  </RoleGrid>
                </Section>

                {/* is_active_officer */}
                {mode === "edit" && (
                  <Section>
                    <SectionHeader className="body-text-regular semi-bold neutral-900">
                      User access
                    </SectionHeader>
                    <ToggleSwitch>
                      <span className="body-text-regular regular neutral-900">
                        Make this user an active officer
                      </span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={isActiveOfficer}
                          onChange={() => setIsActiveOfficer(!isActiveOfficer)}
                        />
                        <span className="slider" />
                      </label>
                    </ToggleSwitch>
                  </Section>
                )}

                {/* is_admin */}
                <Section>
                  <SectionHeader className="body-text-regular semi-bold neutral-900">
                    Admin permission
                  </SectionHeader>
                  <ToggleSwitch>
                    <span className="body-text-regular regular neutral-900">
                      Make this person an admin
                    </span>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={isAdmin}
                        onChange={() => setIsAdmin(!isAdmin)}
                      />
                      <span className="slider" />
                    </label>
                  </ToggleSwitch>
                </Section>

                {/* If edit, show remove user or reset pass */}
                {mode === "edit" && (
                  <ResetWrapper>
                    <IconButton
                      variant="ghost"
                      noPadding={true}
                      icon={<SyncIcon color="#004F96" />}
                      onClick={handleResetPassword}
                    >
                      Reset password
                    </IconButton>
                    <IconButton
                      variant="ghost"
                      isRemove={true}
                      noPadding={true}
                      icon={<DeleteIcon color="#EC2D30" />}
                      onClick={() => {
                        if (onDeleteUser) onDeleteUser();
                      }}
                    >
                      Remove this user
                    </IconButton>
                  </ResetWrapper>
                )}
              </Content>
            </form>
          </FullScreenOverlay>

          {/* Show reset link if needed */}
          {showResetLink && (
            <FullScreenOverlay
              width="480px"
              height="300px"
              isPopup={true}
              popupType="save"
              buttons={[
                {
                  label: "Close",
                  onClick: () => {
                    setShowResetLink(false);
                    setResetLink("");
                    setShowCopiedTooltip(false);
                  },
                },
                {
                  label: "Copy URL",
                  onClick: handleCopyToClipboard,
                  variant: "primary",
                  state: "idle",
                },
              ]}
            >
              <h5 className="semi-bold">Password reset</h5>
              <p>
                Copy the password reset URL and share the lin with the user so
                they can reset their password
              </p>
              {showCopiedTooltip && (
                <ChipStatus autoDismiss={false} status="success">
                  The URL is copied successfully
                </ChipStatus>
              )}
            </FullScreenOverlay>
          )}
        </>
      )}

      {showSaveConfirm && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="info"
          buttons={[
            {
              label: "No, cancel",
              onClick: () => setShowSaveConfirm(false), // Redirect to Dashboard Overview
            },
            {
              label: mode === "add" ? "Yes, add" : "Yes, save",
              onClick: handleConfirmSave,
              variant: "primary",
            },
          ]}
        >
          {mode === "add" ? (
            <h5 className="semi-bold">Add this user?</h5>
          ) : (
            <h5 className="semi-bold">Want to save changes?</h5>
          )}
          <p>
            {mode === "add"
              ? "Are you sure you want to add this user to your organisation?"
              : "Are you sure you want to save the changes you made to this user?"}
          </p>
        </FullScreenOverlay>
      )}
    </>
  );
};

export default UserModal;

/*----------------- Styled Components -----------------*/

const Content = styled.div`
  /* your usual padding */
  padding: 24px;
  /* push the inner content down by exactly the header height */
  /* reserve footer space if needed */
  padding-bottom: 80px;

  display: flex;
  flex-direction: column;
  gap: 24px;

  /* make it scroll within the overlay */
  overflow-y: auto;
  /* contents height without footer and title */
  max-height: calc(100vh - 300px);

  /* include the padding in the height calculation */
  box-sizing: border-box;
`;

const AvatarContainer = styled.div`
  position: relative;
`;

const Avatar = styled.div`
  border-radius: 50%;
  overflow: hidden;
  background: #ccc;
`;

const AddAvatarButton = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translate(370%, -80%);
  border: none;
  cursor: pointer;
  color: transparent;
  background-color: transparent;
  border-radius: 50%;
  width: 24px;
  height: 24px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const RemovePicWrapper = styled.div`
  margin-top: 8px;
  label {
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const FieldGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  input {
    padding: 12px 16px;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  margin-bottom: 8px;
`;

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  label {
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    input {
      margin: 0;
    }
  }
`;

const ToggleSwitch = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    margin-left: 8px;
  }
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
    cursor: pointer;
  }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 34px;
  }
  .slider:before {
    content: "";
    position: absolute;
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
    background-color: #fff;
    transition: 0.4s;
    border-radius: 50%;
  }
  .switch input:checked + .slider {
    background-color: #0065c0;
  }
  .switch input:checked + .slider:before {
    transform: translateX(18px);
  }
`;

const ResetWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`;
